import { Clock, Wifi, WifiOff } from 'lucide-react'
import { CITY_OPTIONS, PRAYER_LIST } from '../../utils/dashboardConstants'

export default function PrayerTimesWidget({ prayerTimes, selectedCity, setSelectedCity, currentTime, isOnline, prayerTimesError }) {
  return (
    <div className="mb-8">
      <div className="relative backdrop-blur-xl bg-white/70 shadow-xl rounded-3xl border border-white/40 p-6 overflow-hidden">
        <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer pointer-events-none"></div>

        <div className="relative">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent flex items-center gap-2">
              <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Jadwal Shalat & Jam
              {isOnline ? (
                <Wifi className="w-4 h-4 text-green-500" title="Terhubung" />
              ) : (
                <WifiOff className="w-4 h-4 text-red-500" title="Tidak ada koneksi" />
              )}
            </h2>
            <select
              value={selectedCity}
              onChange={(e) => setSelectedCity(e.target.value)}
              className="text-xs px-3 py-1.5 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              disabled={!isOnline}
            >
              {CITY_OPTIONS.map(city => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Prayer Times - Left Side */}
            <div className="bg-gradient-to-br from-emerald-50/50 to-teal-50/50 rounded-2xl p-5 border border-white/40">
              <h3 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
                <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                Jadwal Shalat
              </h3>

              {!prayerTimes ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center text-gray-500">
                    <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                    <p className="text-sm">Memuat jadwal shalat...</p>
                  </div>
                </div>
              ) : (
                <>
                  {prayerTimesError && (
                    <div className="mb-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <WifiOff className="w-4 h-4 text-amber-600 flex-shrink-0" />
                        <p className="text-xs text-amber-700 font-medium">{prayerTimesError}</p>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    {PRAYER_LIST.map((prayer) => (
                      <div key={prayer.name} className={`flex items-center justify-between ${prayer.bg} rounded-xl px-4 py-3 hover:shadow-md transition-all duration-200 border border-white/50`}>
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{prayer.icon}</span>
                          <span className={`font-bold text-base ${prayer.color}`}>{prayer.name}</span>
                        </div>
                        <span className="text-base font-bold text-gray-800 tabular-nums">{prayerTimes[prayer.key]}</span>
                      </div>
                    ))}
                  </div>

                  {/* Additional info */}
                  <div className="mt-4 pt-3 border-t border-emerald-200/50">
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <div className="bg-white/60 rounded-lg px-3 py-2 text-center border border-emerald-100">
                        <p className="text-xs text-gray-500 mb-0.5">Imsak</p>
                        <p className="text-sm font-bold text-gray-700 tabular-nums">{prayerTimes.Imsak}</p>
                      </div>
                      <div className="bg-white/60 rounded-lg px-3 py-2 text-center border border-orange-100">
                        <p className="text-xs text-gray-500 mb-0.5">Terbit</p>
                        <p className="text-sm font-bold text-gray-700 tabular-nums">{prayerTimes.Sunrise}</p>
                      </div>
                    </div>
                    <div className="text-center text-xs text-gray-500">
                      📍 {selectedCity} • {new Date().toLocaleDateString('id-ID', { dateStyle: 'long' })}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Digital Clocks - Right Side */}
            <div className="bg-gradient-to-br from-blue-50/50 to-blue-50/50 rounded-2xl p-6 border border-white/40">
              <h3 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
                <Clock className="w-4 h-4 text-blue-600" />
                Jam Dunia
              </h3>
              <div className="space-y-4">
                {/* Jakarta Time */}
                <div className="bg-white/80 rounded-xl p-4 border border-blue-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-600 mb-1">🇮🇩 Jakarta</p>
                      <p className="text-2xl font-bold text-blue-600">
                        {currentTime.toLocaleTimeString('id-ID', {
                          timeZone: 'Asia/Jakarta',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit'
                        })}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">
                        {currentTime.toLocaleDateString('id-ID', {
                          timeZone: 'Asia/Jakarta',
                          weekday: 'short',
                          day: 'numeric',
                          month: 'short'
                        })}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Arab Time */}
                <div className="bg-white/80 rounded-xl p-4 border border-blue-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-600 mb-1">🇸🇦 Riyadh</p>
                      <p className="text-2xl font-bold text-blue-600">
                        {currentTime.toLocaleTimeString('en-SA', {
                          timeZone: 'Asia/Riyadh',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit'
                        })}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">
                        {currentTime.toLocaleDateString('en-SA', {
                          timeZone: 'Asia/Riyadh',
                          weekday: 'short',
                          day: 'numeric',
                          month: 'short'
                        })}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Tokyo Time */}
                <div className="bg-white/80 rounded-xl p-4 border border-pink-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-600 mb-1">🇯🇵 Tokyo</p>
                      <p className="text-2xl font-bold text-pink-600">
                        {currentTime.toLocaleTimeString('ja-JP', {
                          timeZone: 'Asia/Tokyo',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit'
                        })}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">
                        {currentTime.toLocaleDateString('ja-JP', {
                          timeZone: 'Asia/Tokyo',
                          weekday: 'short',
                          day: 'numeric',
                          month: 'short'
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
